<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import axios from 'axios'
import { maxValue, minValue, required, sameAs } from 'vuelidate/lib/validators'

export default {
    name: 'ExtrasForm',
    props: {
        hasSubmitted: { type: Boolean, required: true, default: false },
        dateSubmitted: { type: String, required: false, default: '' },
        mainprice: { type: Number, required: false, default: 0 },
        serviceBreakdown: { type: Array, required: true },
        extras: { type: Array, required: true },
        action: { type: String, required: true }, // POST URL
        csrf: { type: String, required: true }  // GET URL that returns {_token: "..."}
    },
    data() {
        return {
            items: this.extras.map(x => ({
                id: x.id,
                title: x.title,
                price: Number(x.service_cost) || 0,
                hasQty: !!x.has_quantity,
                limit: Number(x.limit) || 0,
                selected: x.selected || false,
                qty: x.quantity || (x.has_quantity ? 0 : 1)
            })),
            total_no_extras: this.mainprice,
            submittedAt: this.dateSubmitted,
            itemsSelected: false, // dummy field for Vuelidate to validate
            // agreeTerms: false,
            sending: false,
            sent: this.hasSubmitted,
            serverResponse: { class: '', message: '' },
            // serverResponse: { class: 'error', message: 'Please fix the quantities.' },
            backHref: document.referrer || '/'
        }
    },

    validations() {
        return {
            // agreeTerms: { $model: sameAs(true) },
            itemsSelected: {
                // not an arrow fn so we can use `this` (Vue 2 Vuelidate)
                // atLeastOne(value) {
                //     return this.items.some(i =>
                //         i.selected && (!i.hasQty || (Number(i.qty) > 0))
                //     )
                // }
            },
        }
    },
    computed: {
        total() {
            return parseFloat(this.total_no_extras) + this.items.reduce((sum, it) => {
                const q = it.selected ? (it.hasQty ? (it.qty || 0) : 1) : 0
                return sum + q * it.price
            }, 0)
        },
        anySelected() {
            return this.items.some(i => i.selected && (!i.hasQty || (i.qty && i.qty > 0)))
        }
    },

    methods: {
        dec(it) {
            if (it.selected && it.hasQty) {
                it.qty = Math.max(0, (it.qty || 0) - 1)
                this.$v.itemsSelected.$touch()
            }
        },
        inc(it) {
            if (!it.selected || !it.hasQty) return
            const lim = it.limit > 0 ? it.limit : Infinity
            it.qty = Math.min(lim, (it.qty || 0) + 1)
            this.$v.itemsSelected.$touch()
        },
        toggle(it) {
            it.selected = !it.selected
            if (it.selected && it.hasQty && (!it.qty || it.qty === 0)) it.qty = 1
            if (!it.selected && it.hasQty) it.qty = 0
            this.$v.itemsSelected.$touch()
        },
        fmt(n) {
            return new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
        },

        hasQtyError(it) {
            if (!it.selected || !it.hasQty) return false
            if (it.qty == null) return true
            const q = Number(it.qty)
            if (!Number.isFinite(q) || q < 1) return true
            if (it.limit && q > it.limit) return true
            return false
        },

        async submit() {

            document.querySelector('body').classList.add('modal-open');

            this.serverResponse = { class: '', message: '' }

            // touch everything once
            this.$v.$touch()

            // you already do your quantity checks; keep those.
            console.log('this.$v.$invalid:', this.$v.$invalid)
            if (this.$v.$invalid) {
                return
            }

            // must select something
            // if (!this.anySelected) {
            //     this.serverResponse = { class: 'error', message: 'Please select at least one extra.' }
            //     return
            // }

            // check quantities
            const bad = this.items.find(it => this.hasQtyError(it))
            if (bad) {
                this.serverResponse = { class: 'error', message: 'Please fix the quantities.' }
                return
            }

            this.sending = true
            try {
                const csrfRes = await axios.get(this.csrf, { withCredentials: true })
                const token = csrfRes?.data?._token || csrfRes?.data?.token || csrfRes?.data?.csrf
                if (!token) throw new Error('CSRF token not received')

                const selected = this.items
                    .filter(it => it.selected)
                    .map(it => ({ id: it.id, quantity: it.hasQty ? Number(it.qty) : 1 }))

                // Τα terms δεν θα υπάρχουν πχια
                // agree_terms: this.agreeTerms ? 1 : 0
                const payload = { _token: token, extras: selected, extras_total: this.total }
                await axios.post(this.action, payload, { withCredentials: true })
                this.sent = true
            } catch (err) {
                console.error(err)
                this.serverResponse = { class: 'error', message: err?.response?.data?.message || err.message || 'Submission failed' }
            } finally {
                this.sending = false
                document.querySelector('body').classList.remove('modal-open');
            }
        },
    },
    watch: {
        sent(val) {
            if (val) {
                this.$nextTick(() => {
                    // move viewport and focus to the success panel for a11y
                    this.$refs.success?.focus()
                    this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                })
            }
        }
    }
}
</script>

<template>
    <form v-if="!sent" :action="action" method="POST" class="extras-form" @submit.prevent="submit">
        <div class="d-flex flex-wrap align-items-start justify-content-center">
            <article v-if="serviceBreakdown.length" class="desc-wrap">
                <h2 class="desc__title">Description</h2>
                <ul class="desc__list">
                    <li v-for="service in serviceBreakdown">
                        <span class="dot"></span>{{ service[0] }}
                        <template v-if="service[1]">
                            <br>
                            <em>{{ service[1] }}</em>
                        </template>
                    </li>
                </ul>
            </article>

            <div class="extras-wrap" v-if="items.length > 0">
                <div class="extras" :class="{ 'has-error': $v.itemsSelected.$error }">
                    <header class="extras__head">
                        <h3>Additional Services</h3>
                    </header>

                    <div v-for="(it, idx) in items" :key="it.id" class="extras__row" :class="{ 'has-qty': it.hasQty }">
                        <span class="extras__label" role="checkbox" :aria-checked="it.selected" tabindex="0" @click="toggle(it)" @keydown.enter.prevent="toggle(it)" @keydown.space.prevent="toggle(it)">
                            <button class="ring" :class="{ 'is-on': it.selected }" type="button" aria-hidden="true" tabindex="-1"></button>
                            {{ it.title }}
                        </span>

                        <div v-show="it.hasQty" class="qty" @click.stop :class="{ 'is-disabled': !it.selected || !it.hasQty || hasQtyError(it) }">
                            <button class="qty__btn" type="button" aria-label="decrease" @click="dec(it)">−</button>
                            <input class="qty__input" type="number" min="0" :max="it.limit || null" :disabled="!it.selected || !it.hasQty" v-model.number="it.qty">
                            <button class="qty__btn" type="button" aria-label="increase" @click="inc(it)">+</button>
                        </div>

                        <span class="extras__price" :class="{ 'is-inactive': !it.selected || hasQtyError(it) }">+ {{ fmt(it.price) }}</span>
                    </div>
                </div>

                <div v-if="$v.itemsSelected.$error" class="error-msg">Please select at least one extra.</div>
            </div>
        </div>

        <!-- Consent + CTA -->
        <!-- <div class="text-center mt-10">
            <label class="consent" :class="{ 'has-error': $v.agreeTerms.$error }">
                <p v-if="$v.agreeTerms.$error" class="error-msg">Please agree to the terms.</p>

                <span>I have read and agreed to terms</span>
                <input type="checkbox" name="agree_terms" v-model="agreeTerms" :true-value="true" :false-value="false" @change="$v.agreeTerms.$touch()" :aria-invalid="$v.agreeTerms.$error ? 'true' : 'false'">
                <span class="checkbox"></span>
            </label>
        </div> -->

        <div class="text-center mt-5">
            <button v-b-modal="'modal-offers-terms'" type="button" class="btn-offers-terms">Additional Information & Terms</button>
        </div>

        <div class="total-wrapper mt-4" role="status" aria-live="polite">
            <span class="total-label">TOTAL:</span>
            <span class="total-value">{{ fmt(total) }}</span>
            <!-- keep a hidden total if you also do server-side fallback -->
            <input type="hidden" name="extras_total" :value="total">
        </div>

        <!-- no-close-on-backdrop no-close-on-esc -->
        <b-modal v-cloak id="modal-offers-terms" size="sm" hide-header hide-footer centered>
            <div class="modal-body-inner">
                <div class="row py-11 px-11">
                    <div class="col-16">
                        <div class="text-terms">
                            <p class="h3">Additional Information & Terms</p>
                            <ul>
                                <li>To confirm a booking a deposit of 30% is required.</li>
                                <li>Photos are delivered 4-5 months from the wedding day.</li>
                                <li>Prices do not include 24% VAT.</li>
                                <li>For destination weddings travel & accommodation expenses not included.</li>
                            </ul>
                        </div>
                    </div>

                    <div class="col-16 text-center mt-4">
                        <button type="button" class="btn btn-primary btn-small" @click="$bvModal.hide('modal-offers-terms')">OK</button>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- inline response/errors (optional) -->
        <div v-if="sending" class="page-overlay">
            <span class="loader"></span>
            <p class="text-center">Finalizing your selection...</p>
        </div>

        <div class="response-wrapper response-wrapper-center mx-auto mb-2" style="max-width: 800px;">
            <div v-if="serverResponse.message" class="response-msg" :class="serverResponse.class">
                <p class="error-msg">{{ serverResponse.message }}</p>
            </div>
        </div>

        <button class="cta mx-auto" type="submit" :disabled="sending">
            <span v-if="!sending">SUBMIT</span>
            <span v-else>SUBMITING…</span>
        </button>
    </form>

    <!-- SUCCESS/ALREADY SENT -->
    <section v-else class="extras-success" role="status" aria-live="polite" tabindex="-1" ref="success">
        <img class="extras-success__icon" src="/public/site/nickyeconomou/dist/images/success.png" alt="">
        <h3 class="extras-success__title mt-5"><b>Thank you!</b></h3>
        <h3 class="extras-success__title">A confirmation email with your selected options has been sent.</h3>

        <a href="https://www.nickyeconomou.com/en" class="btn btn-primary btn-small mt-5">DISCOVER MORE</a>

        <div class="extras-success__actions"></div>
    </section>
</template>

<style scoped lang="scss">
.offer-nonspa .extras__label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
}

.qty.is-disabled {
    opacity: .4;
    pointer-events: none;
}

.extras {
    position: relative;

    &.has-error {
        border-color: red;

        h3 {
            color: red;
        }
    }
}

.consent {
    cursor: pointer;

    &.has-error {
        span {
            color: red;
        }
    }
}
</style>
